// Updated OTP_Verification.tsx - Redesigned Mobile Layout to Match Design
// Key changes: Mobile-first design, fixed bottom button, improved spacing

import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as Logo } from "../../../assets/icons/svg/triva_logo.svg";
import { ArrowLeft } from "lucide-react";
import { API_URLS } from "../../api/urls";
import { AuthStorage } from "./AuthStorage";

interface OTPVerificationProps {
  contactInfo?: string;
  contactType?: "phone" | "email";
  userId?: number;
  expectedOTP?: number;
  onBack?: () => void;
  onVerificationSuccess?: (userData: any) => void;
}

interface OTPVerifyResponse {
  success: string;
  message: string;
  access_token?: string;
  refresh_token?: string;
  user_data: {
    user_id: number;
    user_mobileno: string;
    otp: string;
    user_fname: string;
    user_lname: string;
    goal: string;
    gender: string;
    age: number;
    city: string;
    state: string;
    dob: string;
    height_parameter: string;
    height_value: number;
    weight_parameter: string;
    weight_value: number;
    target_weight: number;
    target_weight_parameter: string;
    food_preference: string;
    progress: string;
    medical_conditions: string;
    last_status: string;
    profile_url: string;
  };
}

const OTPVerification: React.FC<OTPVerificationProps> = ({ 
  contactInfo = "+91 8369752369", 
  contactType = "phone",
  userId,
  expectedOTP,
  onBack,
  onVerificationSuccess
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(90);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Timer countdown effect
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const handleChange = (index: number, value: string) => {
    // iOS autofill/paste can put all digits into one input at once
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6);
      if (digits.length > 1) {
        const newOtp = [...otp];
        digits.split("").forEach((char, i) => { newOtp[i] = char; });
        setOtp(newOtp);
        inputRefs.current[Math.min(digits.length, 5)]?.focus();
      }
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const newOtp = [...otp];
    pasted.split("").forEach((char, i) => {
      newOtp[i] = char;
    });
    setOtp(newOtp);
    const nextEmptyIndex = pasted.length < 6 ? pasted.length : 5;
    inputRefs.current[nextEmptyIndex]?.focus();
  };

  const callOTPVerifyAPI = async (otpValue: string) => {
    try {
      if (!userId) {
        throw new Error("Session expired. Please go back and log in again.");
      }

      const verifyData = {
        user_id: userId,
        otp: otpValue
      };

      console.log("Calling OTP_VERIFY API with data:", verifyData);

      const response = await fetch(API_URLS.OTP_VERIFY, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(verifyData),
      });

      if (!response.ok) {
        let serverMessage = `Verification failed (${response.status})`;
        try {
          const errBody = await response.json();
          serverMessage = errBody.message || errBody.error || serverMessage;
        } catch {
          try {
            const errText = await response.text();
            if (errText) serverMessage = errText;
          } catch { /* ignore */ }
        }
        throw new Error(serverMessage);
      }

      const result: OTPVerifyResponse = await response.json();
      console.log("OTP_VERIFY API response:", result);

      if (result.success === "1") {
        console.log("OTP verification successful:", result.message);

        // If the verify API returns a fresh token, persist it so subsequent
        // API calls (e.g. update_user) use the post-OTP authenticated token.
        if (result.access_token && result.user_data?.user_id) {
          AuthStorage.setAuthData(
            result.access_token,
            result.refresh_token || "",
            result.user_data.user_id,
          );
        }

        if (onVerificationSuccess) {
          onVerificationSuccess(result.user_data);
        }
      } else {
        throw new Error(result.message || "OTP verification failed");
      }

    } catch (error: any) {
      console.error("OTP verification API error:", error);
      setApiError(error.message || "OTP verification failed. Please try again.");
    }
  };

  const handleVerify = async () => {
    const otpValue = otp.join("");
    
    if (otpValue.length !== 6) {
      setApiError("Please enter the complete 6-digit OTP");
      return;
    }

    setApiError("");
    setIsLoading(true);

    try {
      await callOTPVerifyAPI(otpValue);
    } catch (error) {
      console.error("OTP verification error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setTimeLeft(90);
    setOtp(["", "", "", "", "", ""]);
    setApiError("");
    inputRefs.current[0]?.focus();
    
    console.log("OTP resend requested - would need to call USER_LOGIN API again");
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatContactInfo = () => {
    if (contactType === "phone") {
      if (contactInfo && !contactInfo.startsWith("+")) {
        return `+${contactInfo}`;
      }
      return contactInfo;
    }
    return contactInfo;
  };

  const isOTPComplete = otp.every(digit => digit !== "");

  return (
    <div className="min-h-screen bg-white md:min-h-0 flex flex-col md:block">
      {/* Mobile: Logo and title */}
      <div className="md:hidden mb-4">
        <div className="mb-12">
          <Logo className="h-10" />
        </div>

        <div>
          <h1 className="text-[32px] font-bold text-[#282828] mb-2 leading-tight">Verify</h1>
          <p className="text-[17px] text-[#6D6D6D]">OTP Sent to - {formatContactInfo()}</p>
        </div>
      </div>

      {/* Desktop: Back Button */}
      {onBack && (
        <div className="hidden md:block mb-6">
          <button
            onClick={onBack}
            disabled={isLoading}
            className="flex items-center text-[#0DB5B5] hover:text-[#0A9999] transition-colors disabled:opacity-50"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>
      )}

      {/* Main Content Wrapper */}
      <div className="flex-1 md:flex-none flex flex-col md:block pb-[100px] md:pb-0">
        <div className="flex-1 md:flex-none bg-white md:rounded-[18px] rounded-none md:shadow-sm md:border-[0.5px] md:border-[#F2F2F2] p-0 md:p-8 flex flex-col">
          {/* Desktop: Title inside card */}
          <div className="hidden md:block text-center mb-6">
            <h1 className="text-[28px] 3xl:text-[32px] font-bold font-inter text-[#282828] mb-1">
              Verify
            </h1>
            <p className="text-[16px] 3xl:text-[18px] font-medium font-inter text-[#6D6D6D]">
              OTP sent to {formatContactInfo()}
            </p>
          </div>

          {/* Form Content */}
          <div className="space-y-5 flex-1 md:flex-none flex flex-col">
            {/* API Error Display */}
            {apiError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm text-center">{apiError}</p>
              </div>
            )}

            {/* OTP Input Section */}
            <div>
              <label className="block text-[15px] md:text-sm 3xl:text-[14px] text-[#202020] mb-3 md:mb-3 font-normal">
                Enter OTP
              </label>

              {/* OTP Input Fields */}
              <div className="flex justify-between md:justify-center items-center gap-2 md:gap-3 mb-6" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <React.Fragment key={index}>
                    <input
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      type="text"
                      maxLength={6}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      disabled={isLoading}
                      className="w-[40px] h-[48px] md:w-14 md:h-14 text-center text-xl font-semibold bg-[#F2F4F7] rounded-[12px] border border-[#E5E5E5] md:border-[#ABECEB] focus:outline-none focus:ring-0 focus:border-[#0DB5B5] focus:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                      inputMode="numeric"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                    />
                    {index === 2 && (
                      <span className="text-[#6D6D6D] text-2xl font-light px-1">-</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Desktop: Verify Button */}
            <button
              onClick={handleVerify}
              disabled={!isOTPComplete || isLoading}
              className={`hidden md:block w-full text-[16px] 3xl:text-[18px] font-medium py-3 px-4 rounded-[8px] transition-all ${
                !isOTPComplete || isLoading
                  ? 'bg-[#F1FBFB] text-[#00A9A9] opacity-50 cursor-not-allowed'
                  : 'bg-[#06B4B4] text-white hover:bg-[#059999]'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Verifying...
                </div>
              ) : (
                'Verify OTP'
              )}
            </button>

            {/* Resend OTP */}
            <div className="text-right md:text-end">
              {timeLeft > 0 ? (
                <p className="text-[15px] md:text-[12px] 3xl:text-[14px] font-inter text-[#6D6D6D]">
                  Resend OTP?{" "}
                  <span className="text-[#00A9A9] font-medium">
                    {formatTime(timeLeft)}
                  </span>
                </p>
              ) : (
                <button
                  onClick={handleResendOTP}
                  disabled={isLoading}
                  className="text-[15px] md:text-[12px] 3xl:text-[14px] font-inter text-[#00A9A9] hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Resend OTP?
                </button>
              )}
            </div>

            {/* Mobile: Spacer to push button to bottom */}
            <div className="md:hidden flex-1"></div>
          </div>
        </div>

        {/* Mobile: Fixed bottom button */}
        <div className="md:hidden fixed -bottom-0.5 left-0 right-0 p-4" style={{ backdropFilter: "blur(10px)" }}>
          <button
            onClick={handleVerify}
            disabled={!isOTPComplete || isLoading}
            className={`w-full py-4 rounded-xl font-medium text-[16px] transition-colors duration-200 ${
              !isOTPComplete || isLoading
                ? 'bg-[#F1FBFB] text-[#00A9A9] opacity-50 cursor-not-allowed'
                : 'bg-[#0DB5B5] text-white hover:bg-[#0A9999]'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Verifying...
              </div>
            ) : (
              'Verify OTP'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;